import useGetBranch from "@/shared/hooks/useGetBranch";
import useGetEmployeeSchedule from "@/shared/hooks/useGetEmployeeSchedule";
import { useSteps } from "@/shared/hooks/useGetSteps";
import { getAllAvailableSlots } from "@/shared/lib/utils";
import type { Slot } from "@/shared/types/types";
import { Button, Calendar, Card, Divider, Flex, Typography, type CalendarProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { cloneElement, isValidElement, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { type FormValues } from "../../model/form.schema";
import useScheduleScreenStyles from "./ScheduleScreen.styles";
dayjs.extend(advancedFormat);

const ScheduleScreen = () => {
  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      hooks
  // ! ||--------------------------------------------------------------------------------

  const { styles, cx } = useScheduleScreenStyles();
  const { branchData } = useGetBranch();
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const { onNextStep, steps, setSteps } = useSteps();
  const { control, setValue } = useFormContext<FormValues>();
  const date = useWatch({ control, name: "date" });
  const duration = useWatch({ control, name: "duration" });
  const employee_id = useWatch({ control, name: "employee_id" });

  const { employeeScheduleData, loading, refetch } = useGetEmployeeSchedule({
    date: date || dayjs(),
    duration: duration || branchData?.branch.online_booking_durations[0] || 30,
  });

  const scheduleFirst = steps[0].type === "dateTime";

  const isCurrentMonth = currentMonth?.isSame(dayjs(), "month");
  const end_time = useWatch({ control, name: "end_time" });
  const start_time = useWatch({ control, name: "start_time" });

  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      Logic
  // ! ||--------------------------------------------------------------------------------
  const availableSlots = scheduleFirst
    ? getAllAvailableSlots(employeeScheduleData)
    : employeeScheduleData?.employees.find((emp) => emp.employee.id === employee_id)?.slots || [];

  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf("day");
  };

  const handleMonthBackChange = (newMonth: Dayjs) => {
    if (isCurrentMonth) return;
    setCurrentMonth(newMonth);

    setValue("date", date.month(newMonth.month()));
  };
  const handleMonthNextChange = (newMonth: Dayjs) => {
    setCurrentMonth(newMonth);
    setValue("date", date.month(newMonth.month()));
  };
  const fullCellRender: NonNullable<CalendarProps<Dayjs>["fullCellRender"]> = (date, info) => {
    if (!isValidElement<{ className?: string }>(info.originNode)) {
      return info.originNode;
    }
    // эта клетка в этом месяце или нет
    const isActualMonth = date.isSame(currentMonth, "month");

    return cloneElement(info.originNode, {
      className: cx(info.originNode.props.className, !isActualMonth && "foreign-cell"),
    });
  };

  const onSelect = (date: Dayjs) => {
    const today = dayjs().startOf("day");
    const isPast = date.isBefore(today, "day");

    if (!isPast) {
      setValue("date", date);
      setCurrentMonth(date);
    }
  };
  const onDurationSelect = (duration: number) => {
    setValue("duration", duration);
  };
  const onSlotSelect = (slot: Slot) => {
    setValue("end_time", slot.end);
    setValue("start_time", slot.start);
  };

  useEffect(() => {
    if (availableSlots.length) {
      if (!end_time || !start_time) {
        setValue("end_time", availableSlots[0].end);
        setValue("start_time", availableSlots[0].start);
      }
      setSteps((prev) =>
        prev.map((step) => {
          if (step.type === "dateTime") {
            return {
              ...step,
              title: `${date.format("D MMMM, YYYY")}`,
            };
          }
          return step;
        }),
      );
    }
  }, [branchData, employeeScheduleData]);

  useEffect(() => {
    refetch();
  }, [date]);
  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      render
  // ! ||--------------------------------------------------------------------------------

  return (
    <Flex className={styles.screen} vertical gap={8} justify="space-between">
      <Calendar
        rootClassName={styles.calendar}
        fullscreen={false}
        disabledDate={disabledDate}
        fullCellRender={fullCellRender}
        headerRender={() => {
          let month = currentMonth.format("MMMM");
          month = month.charAt(0).toUpperCase() + month.slice(1);
          return (
            <Flex justify="space-between" className="header">
              <svg
                className={cx("back", isCurrentMonth && "no-back")}
                onClick={() => handleMonthBackChange(currentMonth.subtract(1, "month"))}
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{month}</span>
              <svg
                className="forward"
                onClick={() => handleMonthNextChange(currentMonth.add(1, "month"))}
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Flex>
          );
        }}
        value={currentMonth}
        onChange={onSelect}
      />

      <Flex className={styles.rangePicker} vertical gap={16}>
        <Typography.Title level={4} className="pickerTitle">
          Продолжительность
        </Typography.Title>
        <Divider className="rangeDivider" />
        <Flex className="picker" justify="center" gap={8}>
          {branchData?.branch.online_booking_durations.map((dur) => (
            <Flex
              key={dur}
              justify="center"
              className={cx("pickerItem", duration === dur && "picked")}
              onClick={() => onDurationSelect(dur)}
            >
              {dur} минут
            </Flex>
          ))}
        </Flex>
      </Flex>

      {availableSlots.length < 1 && !loading ? (
        <>
          <Flex className="emptySlots" vertical align="center">
            <Typography.Paragraph className="noTime">
              В этот день нет свободного специалиста с выбранной продолжительностью записи
            </Typography.Paragraph>
            {/* <Button className="closeBtn" type="dashed" disabled={true}>
              Перейти к ближайшей дате
            </Button> */}
          </Flex>
        </>
      ) : (
        <Card className={styles.timePicker} loading={loading}>
          <Typography.Title level={4} className="pickerTitle">
            Время
          </Typography.Title>
          <Divider className="rangeDivider" />
          <Flex className="picker" justify="center" gap={8}>
            {availableSlots.map((slot) => (
              <Flex
                key={`${slot.end} ${slot.start}`}
                justify="center"
                className={cx("pickerItem", slot.end === end_time && slot.start === start_time && "picked")}
                onClick={() => onSlotSelect(slot)}
              >
                {slot.start} - {slot.end}
              </Flex>
            ))}
          </Flex>
        </Card>
      )}

      <Flex className={cx("full-width", styles.footer)}>
        <Button
          onClick={() => onNextStep(`${date.format("D MMMM, YYYY")}`)}
          type="primary"
          className="full-width"
          disabled={!(end_time && start_time)}
        >
          Далее
        </Button>
      </Flex>
    </Flex>
  );
};

export default ScheduleScreen;
